/**
 * ステータス定義。
 * docs/15-glossary.md を単一の正とし、値・表示名・アイコンをここに集約する。
 * 他のパッケージはこのファイルを参照し、値やラベルを再定義しない。
 */
const def = (list) => {
    const values = list.map((x) => x.value);
    const byValue = new Map(list.map((x) => [x.value, x]));
    return {
        list,
        values,
        /** 未知の値は「その他」として扱う（docs/05-api-spec.md 第16章） */
        meta: (value) => byValue.get(value) ?? {
            value: value,
            label: 'その他',
            icon: 'circle',
            description: '未知の値',
        },
        label: (value) => byValue.get(value)?.label ?? 'その他',
        is: (value) => typeof value === 'string' && byValue.has(value),
    };
};
export const UserRoles = def([
    { value: 'admin', label: '管理者', icon: 'shield', description: '全体設定・ユーザー管理' },
    { value: 'manager', label: 'マネージャー', icon: 'users', description: 'リスト・施策・実績管理' },
    { value: 'member', label: '担当者', icon: 'user', description: '調査・文面・送信・返信対応' },
]);
/** 権限の強さ。数値が大きいほど広い権限を持つ */
export const ROLE_RANK = { member: 1, manager: 2, admin: 3 };
export const roleAtLeast = (role, min) => ROLE_RANK[role] >= ROLE_RANK[min];
export const UserStatuses = def([
    { value: 'invited', label: '招待中', icon: 'mail', description: 'パスワード未設定' },
    { value: 'active', label: '有効', icon: 'check-circle', description: '利用可能' },
    { value: 'suspended', label: '停止中', icon: 'ban', description: '管理者が停止した' },
]);
export const CompanyStatuses = def([
    { value: 'untouched', label: '未接触', icon: 'circle', description: '収集済みで、まだ送信していない' },
    { value: 'sent', label: '送信済み', icon: 'arrow-up-right', description: '送信が成功した' },
    { value: 'replied', label: '返信あり', icon: 'message-square', description: '自動返信・バウンス以外の返信を受け取った' },
    { value: 'scheduling', label: '日程調整中', icon: 'clock', description: '打ち合わせ日程の調整が始まった' },
    { value: 'appointment', label: 'アポ確定', icon: 'calendar-check', description: '日時が確定した' },
    { value: 'negotiating', label: '商談中', icon: 'handshake', description: '商談を実施し、提案・検討が進行中' },
    { value: 'won', label: '受注', icon: 'check-circle', description: '成約した' },
    { value: 'lost', label: '失注', icon: 'x-circle', description: '断られた、または検討が終了した' },
    { value: 'excluded', label: '対象外', icon: 'ban', description: '送信対象から外した' },
    {
        value: 'unsubscribed',
        label: '配信停止',
        icon: 'ban',
        description: '配信停止リンク・返信・迷惑報告で、今後の連絡が不要と分かった',
    },
]);
/** ファネルの順序。集計の「〜以上に到達」の判定に用いる */
export const COMPANY_STATUS_RANK = {
    untouched: 0,
    sent: 1,
    replied: 2,
    scheduling: 3,
    appointment: 4,
    negotiating: 5,
    won: 6,
    lost: -1,
    excluded: -2,
    unsubscribed: -3,
};
/**
 * 配信停止の登録で「配信停止」に移す状況。商談が進んでいる・受注/失注・対象外は人が決めたものなので動かさない（#129）
 */
export const UNSUBSCRIBE_MOVABLE_STATUSES = ['untouched', 'sent', 'replied'];
/**
 * 企業の統合で残す状況。進んでいるほうを残すが、配信停止は送る前の段階（未接触・送信済み・返信あり）より優先する。
 * ランクだけで選ぶと配信停止（-3）が負けて、除外リストと状況が食い違うため。
 */
export const mergeCompanyStatus = (a, b) => {
    const pick = (u, other) => UNSUBSCRIBE_MOVABLE_STATUSES.includes(other) || other === 'unsubscribed' ? u : other;
    if (a === 'unsubscribed')
        return pick(a, b);
    if (b === 'unsubscribed')
        return pick(b, a);
    return COMPANY_STATUS_RANK[a] > COMPANY_STATUS_RANK[b] ? a : b;
};
/** 状況変更の理由が必須になる遷移（docs/10-screen-spec.md S31） */
export const STATUS_REASON_REQUIRED = ['lost', 'excluded'];
export const ContactTypes = def([
    { value: 'email', label: 'メール', icon: 'mail', description: 'メールアドレス' },
    { value: 'phone', label: '電話', icon: 'phone', description: '電話番号' },
    { value: 'form', label: 'フォーム', icon: 'layout-list', description: '問い合わせフォーム URL' },
    { value: 'fax', label: 'FAX', icon: 'printer', description: 'FAX 番号' },
    { value: 'address', label: '住所', icon: 'map-pin', description: '所在地' },
]);
export const Confidences = def([
    { value: 'high', label: '高', icon: 'check', description: '構造化データ、mailto:/tel: リンク、人の確認済み' },
    { value: 'medium', label: '中', icon: 'minus', description: '表形式の解析、明示的な見出しの下の値' },
    { value: 'low', label: '低', icon: 'help-circle', description: '正規表現のみ、AI 推定' },
]);
export const CONFIDENCE_RANK = { high: 3, medium: 2, low: 1 };
/** 断定表現に使ってよいのは high のみ（docs/15-glossary.md 2.9） */
export const canAssert = (c) => c === 'high';
export const SourceMethods = def([
    { value: 'structured_data', label: '構造化データ', icon: 'file-text', description: 'JSON-LD / microdata' },
    { value: 'table_parse', label: '表解析', icon: 'table', description: '会社概要表の解析' },
    { value: 'regex', label: 'パターン一致', icon: 'search', description: '正規表現による抽出' },
    { value: 'ai_extract', label: 'AI 抽出', icon: 'sparkles', description: 'AI による推定' },
    { value: 'manual', label: '手入力', icon: 'pencil', description: '利用者が入力した' },
    { value: 'csv_import', label: 'CSV 取込', icon: 'upload', description: 'CSV から取り込んだ' },
]);
/** 抽出方法から既定の確度を決める（docs/02-functional-spec.md F04.2） */
export const CONFIDENCE_BY_METHOD = {
    structured_data: 'high',
    table_parse: 'medium',
    regex: 'low',
    ai_extract: 'low',
    manual: 'high',
    csv_import: 'medium',
};
export const Channels = def([
    { value: 'form', label: 'フォーム', icon: 'layout-list', description: '問い合わせフォームから送信' },
    { value: 'email', label: 'メール', icon: 'mail', description: 'メールで送信' },
]);
export const MessageStatuses = def([
    { value: 'draft', label: '下書き', icon: 'pencil', description: '生成・編集中' },
    { value: 'approved', label: '承認済み', icon: 'check', description: '送信可能' },
    { value: 'sending', label: '送信中', icon: 'loader', description: '送信処理を実行中' },
    { value: 'sent', label: '送信済み', icon: 'check-circle', description: '送信に成功した' },
    { value: 'failed', label: '送信失敗', icon: 'x-circle', description: '送信に失敗した' },
    { value: 'unknown', label: '結果不明', icon: 'help-circle', description: '完了を確認できなかった' },
]);
export const SendResults = def([
    { value: 'success', label: '送信成功', icon: 'check', description: '送信の完了を確認できた' },
    { value: 'failed', label: '送信失敗', icon: 'x', description: 'エラーを確認できた' },
    { value: 'unknown', label: '結果不明', icon: 'help-circle', description: '完了を確認できなかった。自動再送しない' },
    { value: 'needs_action', label: '要対応', icon: 'alert-triangle', description: '自動送信できず、人の対応が必要' },
    { value: 'skipped', label: 'スキップ', icon: 'minus', description: '事前チェックで対象から外れた' },
]);
/** 集計の分母に含める送信結果は success のみ（docs/15-glossary.md G05） */
export const COUNTED_SEND_RESULTS = ['success'];
export const TargetStates = def([
    { value: 'pending', label: '送信待ち', icon: 'circle', description: '送信条件を満たせば送信される' },
    { value: 'holding', label: '保留', icon: 'clock', description: '上限・時間帯・承認待ちで待機中' },
    { value: 'sending', label: '送信中', icon: 'loader', description: '送信処理を実行中' },
    { value: 'done', label: '完了', icon: 'check', description: '送信を実行し結果が記録された' },
    { value: 'skipped', label: 'スキップ', icon: 'minus', description: '事前チェックで対象外になった' },
    { value: 'needs_action', label: '要対応', icon: 'alert-triangle', description: '人の対応が必要' },
]);
export const SkipReasons = def([
    { value: 'suppressed', label: '配信停止・営業禁止', icon: 'ban', description: '除外リストに登録されている' },
    { value: 'no_contact', label: '連絡先なし', icon: 'x-circle', description: '有効な連絡先がない' },
    { value: 'recent_contact', label: '直近に接触済み', icon: 'clock', description: '同一ドメイン間隔を満たさない' },
    { value: 'duplicate_in_progress', label: '他施策で送信中', icon: 'copy', description: '他のキャンペーンで進行中' },
    { value: 'not_approved', label: '未承認', icon: 'pencil', description: '文面が承認されていない' },
    { value: 'limit_reached', label: '上限到達', icon: 'gauge', description: '当日の送信上限に達した' },
    { value: 'excluded', label: '対象外', icon: 'ban', description: '企業が対象外に設定されている' },
    { value: 'out_of_hours', label: '時間帯外', icon: 'clock', description: '送信可能時間帯ではない' },
    { value: 'unresolved_template', label: '差し込み未完了', icon: 'pencil', description: '文面に置き換えられていない差し込み項目が残っている' },
]);
export const CampaignStatuses = def([
    { value: 'draft', label: '下書き', icon: 'pencil', description: '作成中。対象未確定' },
    { value: 'scheduled', label: '予約', icon: 'calendar-check', description: '対象確定済みで開始待ち' },
    { value: 'running', label: '実行中', icon: 'loader', description: '送信中' },
    { value: 'paused', label: '一時停止', icon: 'square', description: '停止中。再開できる' },
    { value: 'completed', label: '完了', icon: 'check-circle', description: '全対象の処理が終わった' },
    { value: 'canceled', label: '中止', icon: 'x-circle', description: '途中で中止した。再開しない' },
]);
export const ChannelPolicies = def([
    { value: 'form_first', label: 'フォーム優先', icon: 'layout-list', description: 'フォームがなければメール' },
    { value: 'email_first', label: 'メール優先', icon: 'mail', description: 'メールがなければフォーム' },
    { value: 'form_only', label: 'フォームのみ', icon: 'layout-list', description: 'フォームがなければ送信しない' },
    { value: 'email_only', label: 'メールのみ', icon: 'mail', description: 'メールがなければ送信しない' },
]);
export const GenerationModes = def([
    { value: 'ai', label: 'AI 生成', icon: 'sparkles', description: '企業ごとに AI が文面を作る' },
    {
        value: 'template',
        label: 'テンプレート差し込み',
        icon: 'file-text',
        description: 'テンプレートに企業名などを差し込む。AI を使わない',
    },
    {
        value: 'template_ai',
        label: 'テンプレート＋AI 補完',
        icon: 'sparkles',
        description: 'テンプレートは固定のまま、確認された事項と NeSC の案内だけを企業の事業内容に合わせて AI が書く',
    },
]);
export const ApprovalPolicies = def([
    { value: 'all', label: '全件承認', icon: 'check-circle', description: 'すべての文面を確認してから送信する' },
    { value: 'sample', label: '抜き取り承認', icon: 'check', description: '一部を確認してから送信する' },
    { value: 'none', label: '承認なし', icon: 'alert-triangle', description: '確認せずに送信する' },
]);
export const ReplyClasses = def([
    { value: 'interested', label: '興味あり', icon: 'message-square', description: '商談・資料請求の意向' },
    { value: 'question', label: '質問', icon: 'help-circle', description: '内容確認・条件確認' },
    { value: 'scheduling', label: '日程調整', icon: 'clock', description: '日程の提示・打診' },
    { value: 'not_now', label: '今は不要', icon: 'minus', description: '時期尚早' },
    { value: 'refusal', label: '断り', icon: 'x-circle', description: '明確な拒否' },
    { value: 'unsubscribe', label: '配信停止依頼', icon: 'ban', description: '今後の連絡不可' },
    { value: 'auto_reply', label: '自動返信', icon: 'circle', description: '不在通知・自動応答' },
    { value: 'bounce', label: '配信不能', icon: 'x', description: '配信不能通知' },
    { value: 'other', label: 'その他', icon: 'circle', description: '上記以外' },
]);
export const TrackingEventTypes = def([
    { value: 'open', label: '開封', icon: 'eye', description: '本文の画像が読み込まれた' },
    { value: 'click', label: 'クリック', icon: 'mouse-pointer-click', description: '本文中のリンクを開いた' },
    { value: 'unsubscribe', label: '配信停止', icon: 'ban', description: '配信停止リンクを押した' },
    { value: 'delivered', label: '到達', icon: 'check-circle', description: '相手のサーバーが受け取った' },
    { value: 'bounced', label: '配信不能', icon: 'x-circle', description: '宛先に届かなかった' },
    { value: 'complained', label: '迷惑報告', icon: 'alert-triangle', description: '迷惑メールとして報告された' },
]);
/**
 * 開封は画像の読み込みでしか判定できず、画像を止めている相手では数えられない。
 * 画面では必ずこの注記を添える（実際より少なく出る）。
 */
export const OPEN_TRACKING_NOTE = '開封は画像を読み込んだ場合のみ数えます。画像を表示しない設定の相手は含まれません';
/** 返信として数えない分類（docs/15-glossary.md G01, G02） */
export const NON_REPLY_CLASSES = ['auto_reply', 'bounce'];
export const countsAsReply = (c) => !NON_REPLY_CLASSES.includes(c);
/** 返信分類から企業の営業状況への自動遷移（docs/15-glossary.md 2.3） */
export const STATUS_BY_REPLY_CLASS = {
    interested: 'replied',
    question: 'replied',
    scheduling: 'scheduling',
    not_now: 'replied',
    refusal: 'lost',
    // 配信停止依頼は「対象外」ではなく「配信停止」にする（#129）
    unsubscribe: 'unsubscribed',
    other: 'replied',
};
export const HandlingStatuses = def([
    { value: 'open', label: '未対応', icon: 'alert-triangle', description: '受信済みで、人がまだ確認していない' },
    { value: 'in_progress', label: '対応中', icon: 'loader', description: '返信や社内確認が進行中' },
    { value: 'done', label: '完了', icon: 'check', description: '対応が終わった' },
]);
export const MatchMethods = def([
    { value: 'message_id', label: '返信ヘッダ一致', icon: 'link', description: 'In-Reply-To / References が一致' },
    { value: 'domain', label: 'ドメイン一致', icon: 'link', description: '差出人ドメインが企業ドメインと一致' },
    { value: 'email', label: 'アドレス一致', icon: 'link', description: '差出人が企業の連絡先と一致' },
    { value: 'content', label: '本文一致', icon: 'file-text', description: '本文・署名の企業名や電話番号が一致' },
    { value: 'subject', label: '件名一致', icon: 'file-text', description: '送信件名と一致' },
    { value: 'manual', label: '手動確定', icon: 'pencil', description: '利用者が紐付けた' },
]);
export const AppointmentStatuses = def([
    { value: 'scheduled', label: '予定', icon: 'calendar-check', description: '日時確定、未実施' },
    { value: 'held', label: '実施', icon: 'check-circle', description: '実施済み' },
    { value: 'postponed', label: '延期', icon: 'clock', description: '日程を改める' },
    { value: 'canceled', label: 'キャンセル', icon: 'x-circle', description: '中止になった' },
    { value: 'no_show', label: '不在', icon: 'help-circle', description: '相手が現れなかった' },
]);
export const AppointmentFormats = def([
    { value: 'visit', label: '訪問', icon: 'map-pin', description: '先方または自社を訪問する' },
    { value: 'online', label: 'オンライン', icon: 'video', description: 'Web 会議で実施する' },
    { value: 'phone', label: '電話', icon: 'phone', description: '電話で実施する' },
]);
export const ActivityTypes = def([
    { value: 'note', label: 'メモ', icon: 'file-text', description: '自由記述のメモ' },
    { value: 'call', label: '架電', icon: 'phone', description: '電話での接触' },
    { value: 'manual_send', label: '手動送信', icon: 'arrow-up-right', description: '人が送信した記録' },
    { value: 'next_action', label: '次回アクション', icon: 'clock', description: '期限つきの予定' },
    { value: 'status_change', label: '状況変更', icon: 'circle', description: '営業状況の変更' },
]);
export const JobStatuses = def([
    { value: 'queued', label: '待機中', icon: 'circle', description: '登録済みで実行待ち' },
    { value: 'running', label: '実行中', icon: 'loader', description: '処理中' },
    { value: 'succeeded', label: '完了', icon: 'check-circle', description: '全件正常に処理された' },
    { value: 'partial', label: '一部完了', icon: 'alert-triangle', description: '一部が失敗、または上限で打ち切られた' },
    { value: 'failed', label: '失敗', icon: 'x-circle', description: '処理を継続できなかった' },
    { value: 'canceled', label: '停止', icon: 'square', description: '利用者が停止した' },
]);
export const JOB_TERMINAL_STATUSES = ['succeeded', 'failed', 'canceled', 'partial'];
export const SuppressionTypes = def([
    { value: 'do_not_contact', label: '営業禁止', icon: 'ban', description: '営業行為を行わない' },
    { value: 'unsubscribed', label: '配信停止', icon: 'ban', description: '相手から連絡不要の意思表示があった' },
    { value: 'existing_customer', label: '既存顧客', icon: 'check-circle', description: 'すでに取引がある' },
    { value: 'competitor', label: '同業', icon: 'circle', description: '競合他社' },
    { value: 'manual_exclude', label: '対象外', icon: 'minus', description: '手動で対象から外した' },
]);
export const SuppressionScopes = def([
    { value: 'domain', label: 'ドメイン', icon: 'link', description: 'ドメイン単位で適用' },
    { value: 'email', label: 'メールアドレス', icon: 'mail', description: 'アドレス単位で適用' },
    { value: 'company', label: '企業', icon: 'circle', description: '企業単位で適用' },
    { value: 'phone', label: '電話番号', icon: 'phone', description: '電話番号単位で適用' },
]);
export const AiFeatures = def([
    { value: 'message_generate', label: '文面生成', icon: 'sparkles', description: '初回の営業文面を生成する' },
    { value: 'followup_generate', label: '追客文生成', icon: 'sparkles', description: '追客の文面を生成する' },
    { value: 'security_explain', label: '所見の説明', icon: 'sparkles', description: 'テンプレート＋AI 補完で、所見の説明を企業の事業内容に合わせて書く' },
    { value: 'reply_classify', label: '返信分類', icon: 'sparkles', description: '受信内容を分類する' },
    { value: 'reply_draft', label: '返信下書き', icon: 'sparkles', description: '返信の下書きを作る' },
    { value: 'mail_draft', label: 'メール下書き', icon: 'sparkles', description: 'メーラーで新規・返信・転送の下書きを作る' },
    { value: 'company_summarize', label: '企業要約', icon: 'sparkles', description: '事業内容を要約する' },
    { value: 'security_scan', label: 'セキュリティチェック', icon: 'shield', description: '受動的に収集した信号から Web サイトのセキュリティ状況を評価する' },
    { value: 'suggestion', label: '提案生成', icon: 'sparkles', description: '営業活動の提案を作る' },
]);
export const SecurityRiskLevels = def([
    { value: 'high', label: '高', icon: 'alert-triangle', description: '重大な指摘が含まれる' },
    { value: 'medium', label: '中', icon: 'shield', description: '改善余地のある指摘が含まれる' },
    { value: 'low', label: '低', icon: 'check-circle', description: '大きな指摘はない' },
    { value: 'unknown', label: '判定保留', icon: 'help-circle', description: '情報が足りず評価できない' },
]);
export const SecuritySeverities = def([
    { value: 'high', label: '高', icon: 'alert-triangle', description: '早急に確認すべき指摘' },
    { value: 'medium', label: '中', icon: 'shield', description: '改善を推奨する指摘' },
    { value: 'low', label: '低', icon: 'circle', description: '軽微な指摘' },
    { value: 'info', label: '参考', icon: 'help-circle', description: '事実の確認のみ' },
]);
/** セキュリティチェックの失敗理由コード */
export const SECURITY_SCAN_ERROR_CODES = [
    'no_url',
    'fetch_error',
    'robots_disallow',
    'ssrf_blocked',
    'ai_error',
];
export const SECURITY_SCAN_ERROR_LABELS = {
    no_url: '対象 URL が未設定です',
    fetch_error: 'サイトにアクセスできませんでした',
    robots_disallow: 'robots.txt で許可されていません',
    ssrf_blocked: '安全上の理由で取得しませんでした',
    ai_error: 'AI による評価に失敗しました',
};
export const SuggestionTypes = def([
    { value: 'priority_company', label: '優先企業', icon: 'sparkles', description: '次に接触すべき企業と理由' },
    { value: 'angle', label: '提案の切り口', icon: 'sparkles', description: '企業ごとの訴求点' },
    { value: 'copy_improvement', label: '文面改善', icon: 'sparkles', description: '返信率の低い文面の改善案' },
    { value: 'followup', label: '追客候補', icon: 'sparkles', description: '追客すべき企業と推奨タイミング' },
]);
export const ListTypes = def([
    { value: 'static', label: '静的リスト', icon: 'layout-list', description: '企業を明示的に追加・削除する' },
    { value: 'dynamic', label: '条件リスト', icon: 'search', description: '絞り込み条件を保存し、都度評価する' },
]);
export const MessagePurposes = def([
    { value: 'initial', label: '初回', icon: 'mail', description: '初回の営業文面' },
    { value: 'followup_1', label: '追客1回目', icon: 'mail', description: '1 回目の追客' },
    { value: 'followup_2', label: '追客2回目', icon: 'mail', description: '2 回目の追客' },
    { value: 'reply', label: '返信', icon: 'message-square', description: '受信への返信' },
]);
/** 送信の失敗理由コード（docs/04-data-model.md send_attempts.error_code） */
export const SEND_ERROR_CODES = [
    'captcha_required',
    'login_required',
    'form_not_found',
    'required_field_unknown',
    'iframe_form',
    'attachment_required',
    'timeout',
    'http_error',
    'smtp_error',
    'bounce',
    'browser_crashed',
];
export const SEND_ERROR_LABELS = {
    captcha_required: 'CAPTCHA があり自動送信できません',
    login_required: 'ログインが必要です',
    form_not_found: 'フォームを検出できませんでした',
    required_field_unknown: '必須項目の意味を判別できませんでした',
    iframe_form: 'フォームが外部サービスのため解析できません',
    attachment_required: 'ファイル添付が必須です',
    timeout: '時間内に応答がありませんでした',
    http_error: 'ページを開けませんでした',
    smtp_error: 'メールを送信できませんでした',
    bounce: '配信できませんでした（バウンス）',
    browser_crashed: 'ブラウザの実行が中断されました',
};
/** クロールの失敗理由コード（docs/06-async-jobs.md 4.5） */
export const CRAWL_ERROR_CODES = [
    'robots_disallow',
    'http_404',
    'http_410',
    'http_403',
    'http_5xx',
    'timeout',
    'too_large',
    'parse_error',
    'no_contact',
    'ssrf_blocked',
];
export const CRAWL_ERROR_LABELS = {
    robots_disallow: 'robots.txt で許可されていません',
    http_404: 'ページが見つかりません',
    http_410: 'ページが削除されています',
    http_403: 'アクセスを拒否されました',
    http_5xx: 'サイト側でエラーが発生しました',
    timeout: '時間内に応答がありませんでした',
    too_large: 'ページサイズが上限を超えています',
    parse_error: '内容を解析できませんでした',
    no_contact: '連絡先が見つかりませんでした',
    ssrf_blocked: '安全上の理由で取得しませんでした',
};
//# sourceMappingURL=statuses.js.map